import React, { FocusEvent } from 'react';

import {
  Text,
  Input,
  FormLabel,
  FormControl,
  FormHelperText,
  InputGroup,
} from '@chakra-ui/react';
import { useField } from 'formik';

import { FormikInputProps } from './FormikInput.props';

const FormikInputView = ({
  name,
  type,
  label,
  error,
  helperText,
  labelProps,
  isReadOnly,
  isRequired,
  defaultValue,
  roundUpOnBlur,
  containerProps,
  leftElement,
  rightElement,
  onChange,
  ...props
}: FormikInputProps): JSX.Element => {
  const [field, meta, helpers] = useField(name);

  const numberInputOnWheelPreventChange = (e: any) => {
    e.target.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => e.target.select();

  const handleBlur = () => {
    helpers.setTouched(true);

    if (roundUpOnBlur && type === 'number') {
      const formattedValue = parseFloat(field.value);

      helpers.setValue(formattedValue.toFixed(2));
    }
  };

  return (
    <FormControl mt={3} {...containerProps} isRequired={isRequired}>
      {label && (
        <FormLabel htmlFor={name} {...labelProps} textTransform="capitalize">
          {label}
        </FormLabel>
      )}
      <InputGroup>
        {leftElement}
        <Input
          id={name}
          type={type || name}
          isReadOnly={isReadOnly}
          value={field.value ?? ''}
          aria-describedby={`${name}-helper-text`}
          bg={isReadOnly ? '#F3F3F3' : 'inputColor'}
          isInvalid={meta.touched && meta.error ? true : false}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onWheel={numberInputOnWheelPreventChange}
          onChange={(e) => {
            const rawValue = e.target.value; // Capture raw input
            const value =
              type === 'number' && rawValue !== ''
                ? Number(rawValue)
                : rawValue; // Allow empty string
            helpers.setValue(value);
            onChange && onChange(value); // Pass the raw value to the parent handler
          }}
          {...props}
        />
        {rightElement}
      </InputGroup>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {(error || meta.error) && (
        <Text color="red.500" fontSize="sm">
          {(meta.touched && meta.error) || error}
        </Text>
      )}
    </FormControl>
  );
};

export default FormikInputView;
